import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement | undefined} cartBubbleAccessibleText - The accessible cart count.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return Number(this.refs.cartBubbleCount.dataset.count ?? this.refs.cartBubbleCount.textContent) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.dataset.count = String(value);
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '99+';
    const accessibleText = this.refs.cartBubbleAccessibleText;
    if (accessibleText) accessibleText.textContent = `${accessibleText.dataset.countLabel}: ${value}`;
    const trigger = this.closest('button[aria-label], a[aria-label]');
    if (trigger) trigger.setAttribute('aria-label', trigger.getAttribute('aria-label').replace(/:\s*\d+\s*$/, `: ${value}`));
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const data = event.detail.data ?? {};
    // The server-rendered cart count is authoritative, including partial additions.
    for (const html of Object.values(data.sections ?? {})) {
      if (!html) continue;
      const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
      if (count) {
        this.renderCartBubble(Number(count.textContent) || 0, false);
        return;
      }
    }
    if (data.didError) return;
    const itemCount = Number(data.itemCount) || 0;
    this.renderCartBubble(itemCount, data.source === 'product-form-component');
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    itemCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
